/**
 * 市民端「提交前要素预填」的接口调用点。
 *
 * ★★ 契约**不在这里定义，也没有镜像** ★★
 *   下面 import 的 `@cdfjx/shared/dist/prefill-contract` 就是**后端正在用的那一个文件本身**。
 *   办法与 `api/amend.ts` 完全一致，理由也一样：前后端各写一份「看着一样」的契约，
 *   本项目实测分叉过五处、提交全被 400 顶回来，而谁都不觉得自己写错了。
 *   同一个文件被两端 import，改一处两端同时变，对不上当场编译失败。
 *
 * ★ **不做任何 mock 回落**（市民端红线，见 `api/client.ts` 文件头）。
 *   本模块失败就是抛错，由 `hooks/usePrefill.ts` 决定「拿不到结果 ＝ 五项留空且可填」——
 *   那是**留空**，不是编一份数据糊上去。两者的区别是这条红线的全部内容。
 */

// 深路径而不是裸 `@cdfjx/shared`：裸包名会把 FIELDS／CASE_STATES／编译期断言一并 re-export，
// 本模块就不再是「预填契约的调用点」而变成 shared 的第二个门面。
// 深路径指向的仍是**同一个物理文件**（打包期由 vite.config.ts 的别名指回 src）。
export * from '@cdfjx/shared/dist/prefill-contract';

import {
  CITIZEN_AREAS_ROUTE,
  CITIZEN_PREFILL_ROUTE,
  PREFILL_TIMEOUT_MS,
} from '@cdfjx/shared/dist/prefill-contract';
import type { AreaListResult, PrefillBody, PrefillResult } from '@cdfjx/shared/dist/prefill-contract';
import { apiGet, apiPost } from './client';

/**
 * 前端这一侧的超时。**比服务端那道 8 秒宽一截**，理由见 `apiPost` 的 `timeoutMs` 注释：
 * 两个同值的 8 秒会赛跑，服务端如实报的 `status:'timeout'` 会被前端的 AbortError 吃掉。
 */
const CLIENT_TIMEOUT_MS = PREFILL_TIMEOUT_MS + 4000;

/**
 * 送一段原文去算五项要素。
 *
 * 正常路径下服务端**一定 200**（跑飞、超时、密钥没配都在 `status` 里如实标明，
 * 五项全空返回）；这里会抛的只有「压根没连上／路由不存在／服务端异常」那几种。
 */
export function postPrefill(text: string): Promise<PrefillResult> {
  const body: PrefillBody = { text };
  return apiPost<PrefillResult>(CITIZEN_PREFILL_ROUTE, body, CLIENT_TIMEOUT_MS);
}

/**
 * 区域编码表（协会 GetArea 那 24 条）。
 *
 * ★ **一次会话只拉一次**：这张表在一次填表过程中不会变，而确认页与表单页都要用它。
 *   缓存的是 Promise 而不是结果 —— 两处同时要的时候只会打一次接口。
 * ★ 失败**不缓存**：把失败缓存下来，用户点「重试」就永远重试不动了。
 */
let areasCache: Promise<AreaListResult> | null = null;

export function getAreas(): Promise<AreaListResult> {
  if (areasCache) return areasCache;
  const p = apiGet<AreaListResult>(CITIZEN_AREAS_ROUTE).catch((e: unknown) => {
    areasCache = null;
    throw e;
  });
  areasCache = p;
  return p;
}
